import { isTextSelected, onDelegatedClick, formatDate, getTodayYmd, dmyToYmd, ymdFormat } from '../functions.js';

export default class CalendarElement extends HTMLElement {

    static observedAttributes = [
        'slots'
    ];

    #picker       = null;
    #inputElement = null;
    #slots        = null;
    #currentMonth = null;
    #currentYear = null;

    connectedCallback() {
        this.#inputElement = this.querySelector('input[name=date]');
        this.#currentMonth = (new Date).getMonth() + 1;
        this.#currentYear = (new Date).getFullYear();

        let slots = this.getAttribute('slots');
        if (slots) {
            this.#slots = JSON.parse(slots);
            this.init();
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (newValue) {
            this.#slots = JSON.parse(newValue);
            if (this.#picker) {
                let soonestDate = this.#getSoonestDate();

                this.#enablePickerDates();
                this.#picker.update({
                    selectedDates: [soonestDate] 
                });
            }
        }
    }

    init() {
        this.#picker = new AirDatepicker(this.#inputElement, {
            locale: window.TEXTS.CalendarLocale,
            toggleSelected: false,
            minView: 'days',
            inline: true,
            onRenderCell: ({date, cellType}) => {
                if (cellType === 'day') {
                    date = ymdFormat(date);
                    
                    let isIncluded = this.#isDateIncluded(date);
                    let hasAvailableRegistrations = this.#hasAvailableRegistrations(date);
                    let classes = (isIncluded && !hasAvailableRegistrations) ? '-waitinglist-' : '';

                    let returnValue = {
                        disabled: !isIncluded,
                        classes: classes
                    }

                    return returnValue;
                }
            },
            onChangeViewDate: (viewDate) => {
                this.#currentMonth = viewDate.month + 1;
                this.#currentYear = viewDate.year;
            },
            onSelect: (date) => {
                this.#triggerDateChange();
                this.#setAround();
            },
            // onShow: () => {
            //     console.log('onShow');
            // }
        });

        this.#bindEvents();

        this.#goToSoonest();
        this.#refreshButtons();
    }

    #bindEvents() {

        this.querySelector('.js-today').addEventListener('click', (event) => {
            this.#goToToday();
            event.stopPropagation();
            event.preventDefault();
        });

        this.querySelector('.js-soonest').addEventListener('click', (event) => {
            this.#goToSoonest();
            event.stopPropagation();
            event.preventDefault();
        });

        setTimeout(() => {
            this.querySelector('.air-datepicker-nav--action[data-action=prev]').addEventListener('click', event => {
                let { year, month } = this.#previousYearMonth();
                if (!this.#haveDatesBeforeOrEqual(year, month)) {
                    event.stopPropagation();
                    event.preventDefault();
                }
            });
            this.querySelector('.air-datepicker-nav--action[data-action=next]').addEventListener('click', event => {
                let { year, month } = this.#nextYearMonth();
                if (!this.#haveDatesAfterOrEqual(year, month)) {
                    event.stopPropagation();
                    event.preventDefault();
                }
            });
        }, 1000);

    }

    #goToToday() {
        let now = (new Date);
        this.#picker.setDate(now);
        this.#picker.gotoDate(now);
        this.#triggerDateChange();
    }

    #triggerDateChange() {
        if (!this.#inputElement.value) {
            return;
        }
        let customEvent = new CustomEvent('datechange', { 
            detail: this.#inputElement.value, 
            bubbles: true
        });
        this.dispatchEvent(customEvent);
    }

    #isDateIncluded(date) {
        if (!this.#slots) { return false; }

        let slot = this.#slots.find(slot => {
            return slot.slot_date === date;
        });

        return slot ? true : false;
    }

    #hasAvailableRegistrations(date) {
        if (!this.#slots) { return false; }

        let slot = this.#slots.find(slot => {
            return slot.slot_date === date && slot.available_for_context > 0;
        });

        return slot ? true : false;
    }

    #setAround() {

        this.querySelectorAll('.air-datepicker-cell.-around-').forEach(cell => {
            cell.classList.remove('-around-');
        });

        let dayElement = this.querySelector('.air-datepicker-cell.-selected-');

        let precedingDays = parseInt(this.dataset.precedingDays || 0, 10);
        let followingDays = parseInt(this.dataset.followingDays || 0, 10);

        for (let i = 1; i <= precedingDays; i++) {
            let precedingDay = dayElement.previousElementSibling;
            if (precedingDay) {
                precedingDay.classList.add('-around-');
                dayElement = precedingDay;
            }
        }

        dayElement = this.querySelector('.air-datepicker-cell.-selected-');

        for (let i = 1; i <= followingDays; i++) {
            let precedingDay = dayElement.nextElementSibling;
            if (precedingDay) {
                precedingDay.classList.add('-around-');
                dayElement = precedingDay;
            }
        }

    }

    #enablePickerDates() {
        let dates = this.#getDates();
        this.#picker.enableDate(dates);
    }

    #getDates() {
        return this.#slots.map(slot => slot.slot_date);
    }

    #getSoonestDate() {
        let todayYmd = getTodayYmd();

        let gotoDate = null;

        let dates = this.#getDates();

        for (let i = 0; i < dates.length; i++) {
            if (dates[i] >= todayYmd) {
                gotoDate = dates[i];
                break;
            }
        }

        if (gotoDate === null && dates[dates.length - 1]) {
            gotoDate = dates[dates.length - 1];
        }

        return gotoDate;
    }

    #goToSoonest() {

        let gotoDate = this.#getSoonestDate();

        this.#picker.selectDate(gotoDate);
        this.#triggerDateChange();
    }

    #refreshButtons() {
        this.querySelector('.js-today').style.display = this.#isTodayAvailable() ? 'inline' : 'none';
        this.querySelector('.js-soonest').style.display = 'inline';
    }

    #isTodayAvailable() {
        let today = getTodayYmd();
        return this.#isDateIncluded(today);
    }

    #nextYearMonth() {
        return this.#currentMonth === 12 ? { year: this.#currentYear + 1, month: 1 } : { year: this.#currentYear, month: this.#currentMonth + 1 };
    }

    #previousYearMonth() {
        return this.#currentMonth === 1 ? { year: this.#currentYear - 1, month: 12 } : { year: this.#currentYear, month: this.#currentMonth - 1 };
    }

    #haveDatesAfterOrEqual(year, month) {
        let dates = this.#getDates();
        return dates.some(date => {
            let parts = date.split('-');
            return parseInt(parts[0]) >= year && (parseInt(parts[0]) > year || parseInt(parts[1]) >= month);
        });
    }

    #haveDatesBeforeOrEqual(year, month) {
        let dates = this.#getDates();
        return dates.some(date => {
            let parts = date.split('-');
            return parseInt(parts[0]) <= year && (parseInt(parts[0]) < year || parseInt(parts[1]) <= month);
        });
    }

}
window.customElements.define('calendar-element', CalendarElement);

